import { Component } from 'preact';
import {
  DIALOG_STYLE, TEXT_PRIMARY, TEXT_BODY,
  GOLD, GOLD_BTN, BEVELED_BTN, FourCorners,
} from '../../lib/medievalTheme';
import { markReady, unmarkReady } from '../../utils/arena/instancesApi';
import { GAME_MODES } from '../../utils/arena/gameModes';
import { t } from '../../localization/t';

// Global overlay that fires when a participant receives a
// 'readyCheckStarted' lifecycle event on an instance they're in.
// Counts down 60s; calls markReady on confirm, unmarkReady on
// expiry or cancel. The server-side handler transitions the
// instance to Active when every participant has readied.
const READY_WINDOW_MS = 60_000;

export default class ReadyCheckModal extends Component {
  constructor(props) {
    super(props);
    const deadline = props.instance?.ready_check_deadline
      ? new Date(props.instance.ready_check_deadline).getTime()
      : Date.now() + READY_WINDOW_MS;
    this._deadline = deadline;
    this.state = {
      secondsLeft: Math.max(0, Math.ceil((deadline - Date.now()) / 1000)),
      confirmed: !!props.alreadyReady,
    };
  }

  componentDidMount() {
    this.iv = setInterval(this.tick, 1000);
  }

  componentWillUnmount() {
    clearInterval(this.iv);
  }

  tick = () => {
    const left = Math.max(0, Math.ceil((this._deadline - Date.now()) / 1000));
    this.setState((s) => {
      if (left <= 0) {
        clearInterval(this.iv);
        if (!s.confirmed) this.handleExpire();
        return { secondsLeft: 0 };
      }
      return { secondsLeft: left };
    });
  };

  handleConfirm = async () => {
    this.setState({ confirmed: true });
    try {
      if (this.props.onReady) {
        await this.props.onReady();
      } else {
        await markReady(this.props.instance.id);
      }
    } catch (err) {
      console.error('[ReadyCheck] markReady failed:', err);
    }
    // Keep the modal visible in "waiting for others" state until
    // the parent dismisses it (on phaseTransition → active event
    // for instances, or draft:match_start for draft rounds).
  };

  handleExpire = async () => {
    try {
      if (this.props.onUnready) await this.props.onUnready();
      else await unmarkReady(this.props.instance.id);
    } catch {}
    this.props.onClose?.();
  };

  handleCancel = () => {
    this.handleExpire();
  };

  render() {
    const { instance } = this.props;
    const { secondsLeft, confirmed } = this.state;
    const mode = GAME_MODES.find((m) => m.key === instance.mode);
    const modeName = mode?.title ? t(mode.title) : instance.mode;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)' }}>
        <div className="relative w-[27.5cqw] flex flex-col p-6 gap-4" style={DIALOG_STYLE}>
          <FourCorners />
          <h2 className="arena-heading text-xl font-bold text-center" style={{ color: TEXT_PRIMARY }}>
            {confirmed ? t('Waiting for others…') : t('Ready to play?')}
          </h2>
          <p className="text-sm text-center" style={{ color: TEXT_BODY }}>
            {confirmed
              ? t('Your {mode} starts as soon as every player confirms.', { mode: modeName })
              : t('Your {mode} is ready. Confirm within {s}s or your seat will reopen.', { mode: modeName, s: secondsLeft })}
          </p>
          <div className="flex justify-center gap-3">
            <button
              type="button"
              className="px-4 py-2 text-sm cursor-pointer"
              style={BEVELED_BTN}
              onClick={this.handleCancel}
            >
              {confirmed ? t('Leave') : t('Cancel')}
            </button>
            <button
              type="button"
              className="px-6 py-2 text-sm font-semibold cursor-pointer"
              style={GOLD_BTN}
              disabled={confirmed}
              onClick={this.handleConfirm}
            >
              {confirmed
                ? t('Ready. Waiting')
                : t('Enter {mode} ({s}s)', { mode: mode?.title ? t(mode.title) : t('Match'), s: secondsLeft })}
            </button>
          </div>
        </div>
      </div>
    );
  }
}
